// ===================================================================
// PA Grants — Milestone Templates (Admin Settings)
// Manage the default milestone list used when starting a new project.
// Stored in localStorage: pa-milestone-templates-v1
// ===================================================================

import { useState, useEffect } from 'react';
import {
  Title, Text, Paper, Button, Group, TextInput,
  Stack, ActionIcon, Badge,
} from '../../components/gab/GabComponents';
import { IconPlus, IconTrash, IconPencil, IconArrowUp, IconArrowDown, IconRefresh, IconDeviceFloppy } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';

const STORAGE_KEY = 'pa-milestone-templates-v1';

export interface MilestoneTemplate {
  id: string;
  name: string;
  sortOrder: number;
}

const DEFAULTS: MilestoneTemplate[] = [
  { id: 'tpl-1',  name: 'Damage site inspection',        sortOrder: 1  },
  { id: 'tpl-2',  name: 'Project formulation complete',  sortOrder: 2  },
  { id: 'tpl-3',  name: 'Grant application submitted',   sortOrder: 3  },
  { id: 'tpl-4',  name: 'FEMA obligation received',      sortOrder: 4  },
  { id: 'tpl-5',  name: 'Notice to proceed issued',      sortOrder: 5  },
  { id: 'tpl-6',  name: 'Work start',                    sortOrder: 6  },
  { id: 'tpl-7',  name: '50% completion',                sortOrder: 7  },
  { id: 'tpl-8',  name: 'Substantial completion',        sortOrder: 8  },
  { id: 'tpl-9',  name: 'Final inspection',              sortOrder: 9  },
  { id: 'tpl-10', name: 'Closeout package submitted',    sortOrder: 10 },
];

export function loadTemplates(): MilestoneTemplate[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return DEFAULTS;
}

function saveTemplates(templates: MilestoneTemplate[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

export default function MilestoneTemplates() {
  const [templates, setTemplates] = useState<MilestoneTemplate[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [addName, setAddName] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setTemplates(loadTemplates());
  }, []);

  const persist = (next: MilestoneTemplate[]) => {
    setTemplates(next);
    saveTemplates(next);
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...templates];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    persist(next.map((t, i) => ({ ...t, sortOrder: i + 1 })));
  };

  const moveDown = (idx: number) => {
    if (idx === templates.length - 1) return;
    const next = [...templates];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    persist(next.map((t, i) => ({ ...t, sortOrder: i + 1 })));
  };

  const startEdit = (t: MilestoneTemplate) => {
    setEditingId(t.id);
    setEditName(t.name);
  };

  const saveEdit = () => {
    if (!editName.trim()) return;
    persist(templates.map(t => t.id === editingId ? { ...t, name: editName.trim() } : t));
    setEditingId(null);
    notifications.show({ message: 'Milestone updated', color: 'var(--color-success)' });
  };

  const remove = (id: string) => {
    persist(templates.filter(t => t.id !== id).map((t, i) => ({ ...t, sortOrder: i + 1 })));
  };

  const addNew = () => {
    if (!addName.trim()) return;
    const next: MilestoneTemplate = {
      id: `tpl-${Date.now()}`,
      name: addName.trim(),
      sortOrder: templates.length + 1,
    };
    persist([...templates, next]);
    setAddName('');
    setAdding(false);
    notifications.show({ message: 'Milestone added', color: 'var(--color-success)' });
  };

  const resetDefaults = () => {
    persist(DEFAULTS);
    notifications.show({ message: 'Reset to defaults', color: 'var(--color-primary-700)' });
  };

  return (
    <Stack gap={12} style={{ maxWidth: 700 }}>
      <Group justify="space-between">
        <div>
          <Title order={3}>Milestone Templates</Title>
          <Text size="sm" c="dimmed">
            Default milestones added to new projects via "From Template". Reorder, rename, or delete as needed.
          </Text>
        </div>
        <Group gap="xs">
          <Button size="xs" variant="subtle" leftSection={<IconRefresh size={12} />} onClick={resetDefaults}>
            Reset to defaults
          </Button>
          <Button size="xs" leftSection={<IconPlus size={12} />} onClick={() => setAdding(true)}>
            Add Milestone
          </Button>
        </Group>
      </Group>

      <Paper withBorder>
        <Stack gap={0}>
          {templates.map((t, idx) => (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px',
              borderBottom: idx < templates.length - 1 ? '1px solid #f1f5f9' : undefined,
              background: editingId === t.id ? '#f5f4ff' : '#fff',
            }}>
              {/* Order badge */}
              <Badge size="sm" variant="light" color="#94a3b8" style={{ minWidth: 28, fontFamily: 'monospace' }}>
                {idx + 1}
              </Badge>

              {/* Name / edit field */}
              {editingId === t.id ? (
                <TextInput
                  size="xs"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
                  style={{ flex: 1 }}
                  autoFocus
                />
              ) : (
                <Text size="sm" style={{ flex: 1 }}>{t.name}</Text>
              )}

              {/* Actions */}
              {editingId === t.id ? (
                <Group gap={4}>
                  <Button size="xs" leftSection={<IconDeviceFloppy size={12} />} onClick={saveEdit}
                    disabled={!editName.trim()}>Save</Button>
                  <Button size="xs" variant="subtle" onClick={() => setEditingId(null)}>Cancel</Button>
                </Group>
              ) : (
                <Group gap={4}>
                  <ActionIcon size="xs" variant="subtle" color="#94a3b8" disabled={idx === 0} onClick={() => moveUp(idx)}>
                    <IconArrowUp size={12} />
                  </ActionIcon>
                  <ActionIcon size="xs" variant="subtle" color="#94a3b8" disabled={idx === templates.length - 1} onClick={() => moveDown(idx)}>
                    <IconArrowDown size={12} />
                  </ActionIcon>
                  <ActionIcon size="xs" variant="subtle" color="var(--color-primary-700)" onClick={() => startEdit(t)}>
                    <IconPencil size={12} />
                  </ActionIcon>
                  <ActionIcon size="xs" variant="subtle" color="var(--color-error)" onClick={() => remove(t.id)}>
                    <IconTrash size={12} />
                  </ActionIcon>
                </Group>
              )}
            </div>
          ))}

          {/* Inline add row */}
          {adding && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, padding: '10px 14px', background: '#f8fafc', borderTop: '1px dashed #cbd5e1' }}>
              <TextInput
                size="xs"
                placeholder="New milestone name"
                value={addName}
                onChange={e => setAddName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addNew(); if (e.key === 'Escape') setAdding(false); }}
                style={{ flex: 1 }}
                autoFocus
              />
              <Button size="xs" onClick={addNew} disabled={!addName.trim()}>Add</Button>
              <Button size="xs" variant="subtle" onClick={() => { setAdding(false); setAddName(''); }}>Cancel</Button>
            </div>
          )}

          {templates.length === 0 && !adding && (
            <Text size="sm" c="dimmed" p="md">No templates. Add one or reset to defaults.</Text>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
